"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactPopper = require("react-popper");
var _useOnClickOutside = _interopRequireDefault(require("../../hooks/useOnClickOutside"));
var _useToggler2 = _interopRequireDefault(require("../../hooks/useToggler"));
var _useTrigger2 = _interopRequireDefault(require("../../hooks/useTrigger"));
var _excluded = ["isOpen", "trigger", "children", "onClick", "align", "withIcon", "className", "disabled", "tabIndex"];
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/**
 * The Dropdown component provides a way to display information in a <em>dropbox</em> anchored to its trigger.
 */
var Dropdown = function Dropdown(_ref) {
  var controlledIsOpen = _ref.isOpen,
    initialTrigger = _ref.trigger,
    children = _ref.children,
    onClick = _ref.onClick,
    align = _ref.align,
    withIcon = _ref.withIcon,
    className = _ref.className,
    disabled = _ref.disabled,
    _ref$tabIndex = _ref.tabIndex,
    tabIndex = _ref$tabIndex === void 0 ? '0' : _ref$tabIndex,
    dropdownAttributes = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _React$useState = _react.default.useState(null),
    triggerRef = _React$useState[0],
    setTriggerRef = _React$useState[1];
  var _React$useState2 = _react.default.useState(null),
    popperRef = _React$useState2[0],
    setPopperRef = _React$useState2[1];
  var popperContainerRef = _react.default.useRef(null);
  var _React$useState3 = _react.default.useState(null),
    arrowRef = _React$useState3[0],
    setArrowRef = _React$useState3[1];
  var _useToggler = (0, _useToggler2.default)(false),
    isOpen = _useToggler.isOpen,
    setOpen = _useToggler.setOpen,
    setClose = _useToggler.setClose,
    setToggle = _useToggler.setToggle;
  (0, _useOnClickOutside.default)(popperContainerRef, setClose);
  var alignPopper = "bottom-" + (align === 'left' ? 'start' : 'end');
  var alignFallbackPlacements = align === 'left' ? ['top-start', 'bottom-start'] : ['top-end', 'bottom-end'];
  var _usePopper = (0, _reactPopper.usePopper)(triggerRef, popperRef, {
      placement: alignPopper,
      modifiers: [{
        name: 'arrow',
        options: {
          element: arrowRef
        }
      }, {
        name: 'offset',
        options: {
          offset: [0, 1]
        }
      }, {
        name: 'flip',
        options: {
          fallbackPlacements: alignFallbackPlacements
        }
      }]
    }),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes,
    forceUpdate = _usePopper.forceUpdate;
  // const { trigger } = useTrigger(initialTrigger, '', { tabIndex: 0 });
  var _useTrigger = (0, _useTrigger2.default)(initialTrigger, {
      tabIndex: '-1',
      // 'aria-labelledby': 'text-button',
      'aria-haspopup': 'listbox',
      'aria-expanded': isOpen ? true : null
      // id: 'text-button',
    }, 'button'),
    trigger = _useTrigger.trigger;
  _react.default.useEffect(function () {
    if (!disabled) {
      if (controlledIsOpen) {
        setOpen();
        forceUpdate && forceUpdate();
      } else {
        setClose();
      }
    }
  }, [controlledIsOpen, setClose, setOpen, disabled, forceUpdate]);
  var escHandle = _react.default.useCallback(function (e) {
    if (e.keyCode === 27) {
      setClose();
    }
  }, [setClose]);
  _react.default.useEffect(function () {
    document.addEventListener('keydown', escHandle);
    return function () {
      document.removeEventListener('keydown', escHandle);
    };
  }, [escHandle]);
  var toggleDropdown = function toggleDropdown(event) {
    if (!disabled) {
      setToggle();
      forceUpdate();
      if (onClick && typeof onClick === 'function') {
        onClick(event);
      }
    }
  };
  var dropdownClasses = (0, _clsx.default)('uk-dropdown', isOpen ? 'open' : '', className);
  var bodyClasses = (0, _clsx.default)('uk-dropdown__body', align === 'left' ? '' : 'uk-dropdown__body--right');
  return /*#__PURE__*/_react.default.createElement("div", {
    className: dropdownClasses,
    ref: popperContainerRef
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-dropdown__control"
  }, /*#__PURE__*/_react.default.createElement("div", {
    ref: setTriggerRef,
    className: "uk-dropdown__head",
    onClick: toggleDropdown,
    onKeyPress: toggleDropdown,
    tabIndex: disabled ? '-1' : tabIndex
  }, trigger, withIcon && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-dropdown__indicator"
  }, /*#__PURE__*/_react.default.createElement("div", {
    "aria-hidden": "true",
    className: "uk-svg"
  }, /*#__PURE__*/_react.default.createElement("svg", {
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
    transform: "rotate(" + (isOpen && attributes != null && attributes.popper && (attributes == null ? void 0 : attributes.popper['data-popper-placement'].split('-')[0]) === 'top' ? 180 : 0) + ")"
  }, /*#__PURE__*/_react.default.createElement("path", {
    d: "M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
  })))))), /*#__PURE__*/_react.default.createElement("div", _extends({
    className: "uk-dropdown__container",
    ref: setPopperRef,
    style: styles.popper,
    "aria-labelledby": "text-button"
  }, attributes.popper, dropdownAttributes, {
    tabIndex: 0
  }), /*#__PURE__*/_react.default.createElement("div", {
    className: bodyClasses
    // tabIndex={isOpen ? '0' : '-1'}
    ,
    role: "listbox"
  }, children || 'empty'), /*#__PURE__*/_react.default.createElement("div", {
    ref: setArrowRef,
    style: styles.arrow,
    className: "uk-dropdown__arrow"
  })));
};
Dropdown.propTypes = {
  /**
   * Show or hide the Dropdown (controlled)
   */
  isOpen: _propTypes.default.bool,
  /**
   * Dropdown's trigger
   */
  trigger: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Position of the Dropdown's dropbox, relative to its trigger
   */
  align: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Display the default icon
   */
  withIcon: _propTypes.default.bool,
  /**
   * Callback function onClick
   */
  onClick: _propTypes.default.func,
  /**
   * Set the Dropdown as disabled
   */
  disabled: _propTypes.default.bool
};
Dropdown.defaultProps = {
  trigger: 'Dropdown',
  isOpen: false,
  withIcon: true,
  align: 'left'
};
var _default = Dropdown;
exports.default = _default;